import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useCart } from '../../context/CartContext';
import { toast } from '../../hooks/use-toast';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import Footer from '../../components/Footer';
import { Star } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitize';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockModalData, setStockModalData] = useState({ requested: 0, available: 0 });
  const [qtyInCart, setQtyInCart] = useState(0);
  const [hasRestockRequest, setHasRestockRequest] = useState(false);
  const [requestingRestock, setRequestingRestock] = useState(false);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Product placeholder image
  const productPlaceholder = '/product-placeholder.jpg';

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/shop/products/${slug}`);
      if (!res.ok) throw new Error('Product not found');
      const data = await res.json();
      setProduct(data);
      setSelectedVariantIndex(0);
      
      // Run all checks in parallel before rendering
      await Promise.all([
        // Fetch reviews
        data._id ? fetchReviews(data._id) : Promise.resolve(),
        // Check cart quantity
        checkCartQuantity(data._id),
        // Check restock request status (important for button state)
        checkRestockRequest(data._id)
      ]);
    } catch (e) {
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [slug, API_BASE]);

  useEffect(() => {
    fetchProduct();
    
    // Refresh product data when page regains focus (e.g., after completing purchase)
    const handleFocus = () => {
      fetchProduct();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchProduct]);
  
  const checkCartQuantity = async (productId) => {
    try {
      const cartId = localStorage.getItem('cart_id');
      if (!cartId) {
        setQtyInCart(0);
        return;
      }
      
      const token = localStorage.getItem('token');
      const headers = { 'X-Cart-Id': cartId };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(`${API_BASE}/api/cart`, { headers });
      if (res.ok) {
        const cart = await res.json();
        const item = cart.items?.find(item => item.product_id === productId);
        setQtyInCart(item ? item.quantity : 0);
      }
    } catch (error) {
      console.error('Error checking cart:', error);
      setQtyInCart(0);
    }
  };

  const fetchReviews = async (productId) => {
    setReviewsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/shop/products/${productId}/reviews`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } catch (e) {
      console.error('Failed to fetch reviews:', e);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    console.log('=== ADD TO CART STARTED ===');
    console.log('Product:', product);
    console.log('Variant:', variant);
    console.log('Quantity:', qty);
    
    if (!product || !product._id) {
      console.error('Product data missing:', product);
      toast({
        title: 'Error',
        description: 'Product information is missing',
        variant: 'destructive'
      });
      return;
    }
    
    // Check stock before adding
    const availableStock = variant?.inventory_qty || 0;
    if (availableStock === 0) {
      toast({
        title: 'Out of Stock',
        description: 'This product is currently out of stock',
        variant: 'destructive'
      });
      return;
    }
    
    // Calculate how many can still be added (considering cart)
    const maxCanAdd = availableStock - qtyInCart;
    
    if (maxCanAdd <= 0) {
      // Already have max in cart
      setStockModalData({
        requested: qty,
        available: 0,
        inCart: qtyInCart,
        totalStock: availableStock,
        productTitle: product.title
      });
      setShowStockModal(true);
      return;
    }
    
    // Show modal if quantity exceeds what can be added
    if (qty > maxCanAdd) {
      setStockModalData({
        requested: qty,
        available: maxCanAdd,
        inCart: qtyInCart,
        totalStock: availableStock,
        productTitle: product.title
      });
      setShowStockModal(true);
      setQty(maxCanAdd); // Auto-adjust quantity
      return; // Don't add to cart yet, let user confirm via modal
    }

    const result = await addToCart(product._id, variant?.id, qty);
    
    console.log('=== ADD TO CART RESULT ===', result);

    if (result && result.success) {
      // Update cart quantity display
      setQtyInCart(qtyInCart + qty);
      toast({
        title: 'Success',
        description: 'Item added to cart'
      });
    } else {
      toast({
        title: 'Error',
        description: result?.error || 'Failed to add to cart',
        variant: 'destructive'
      });
    }
  };

  const checkRestockRequest = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHasRestockRequest(false);
        return;
      }
      
      const res = await fetch(`${API_BASE}/api/shop/products/${productId}/my-restock-request`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasRestockRequest(data.has_request);
      }
    } catch (error) {
      console.error('Error checking restock request:', error);
    }
  };

  const handleRequestRestock = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to request stock notifications',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }
    
    setRequestingRestock(true);
    try {
      const res = await fetch(`${API_BASE}/api/shop/products/${product._id}/request-restock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setHasRestockRequest(true);
        toast({
          title: 'Request Submitted!',
          description: data.message || "We'll notify you when this product is back in stock",
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to submit request',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setRequestingRestock(false);
    }
  };
  
  // Handle adding after stock modal confirmation
  const handleStockModalConfirm = async () => {
    setShowStockModal(false);
    
    if (stockModalData.available <= 0) {
      // Can't add any more
      toast({
        title: 'Cannot Add More',
        description: 'You already have the maximum available quantity in your cart',
        variant: 'destructive'
      });
      return;
    }
    
    const result = await addToCart(product._id, variant?.id, stockModalData.available);
    
    if (result && result.success) {
      setQtyInCart(qtyInCart + stockModalData.available);
      toast({
        title: 'Success',
        description: `Added ${stockModalData.available} units to cart`
      });
    } else {
      toast({
        title: 'Error',
        description: result?.error || 'Failed to add to cart',
        variant: 'destructive'
      });
    }
  };

  const handleBuyNow = async () => {
    const variantId = variant?.id || variant?.sku || null;
    const result = await addToCart(product._id, variantId, qty);
    
    if (result.success) {
      navigate('/checkout');
    } else {
      toast({
        title: 'Error',
        description: result.error || 'Failed to add to cart',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <>
        <ResponsiveHeader />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <p className="dark:text-gray-100">Loading...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <ResponsiveHeader />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <p className="dark:text-gray-100">Product not found</p>
        </div>
        <Footer />
      </>
    );
  }

  const variant = product.variants?.[selectedVariantIndex];

  return (
    <>
      <ResponsiveHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Gallery */}
          <Card className="overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="p-0">
              <div className="aspect-square bg-white dark:bg-gray-700 overflow-hidden">
                <img 
                  src={(() => {
                    const mediaPath = (product.media && product.media[0]);
                    if (mediaPath && !mediaPath.startsWith('http') && mediaPath !== '/product-placeholder.jpg') {
                      // Handle different URL formats for Kubernetes routing
                      let cleanPath = mediaPath;
                      if (mediaPath.startsWith('/uploads/')) {
                        // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
                        cleanPath = mediaPath.replace('/uploads/', '/api/uploads/general/');
                      } else if (!mediaPath.startsWith('/api/')) {
                        // Unknown format: prepend /api
                        cleanPath = `/api${mediaPath}`;
                      }
                      // If already starts with /api/, use as-is
                      return `${API_BASE}${cleanPath}`;
                    }
                    return mediaPath || productPlaceholder;
                  })()} 
                  alt={product.title} 
                  className={`w-full h-full ${
                    (!product.media || product.media.length === 0) 
                      ? 'object-contain p-8' 
                      : 'object-cover'
                  }`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = productPlaceholder;
                    // Change to contain when placeholder is used
                    e.target.className = e.target.className.replace('object-cover', 'object-contain p-8');
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <div>
            <h1 className="text-3xl font-black mb-2 dark:text-gray-100">{product.title}</h1>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Vendor: {product.vendor || '—'} • SKU: {variant?.sku || '—'}</div>
            
            {/* Rating Display */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-5 h-5 ${
                      star <= (product.rating || 0) 
                        ? 'fill-yellow-400 text-yellow-400' 
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  />
                ))}
                <span className="ml-2 text-lg font-semibold dark:text-gray-200">{product.rating?.toFixed(1) || '0.0'}</span>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">({product.review_count || 0} reviews)</span>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <span className="text-3xl font-black text-red-600 dark:text-purple-400">${variant?.price?.toFixed(2) || product.min_price?.toFixed(2)} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">CAD</span></span>
              {variant?.compare_at_price && (
                <span className="line-through text-gray-400">${variant.compare_at_price.toFixed(2)} CAD</span>
              )}
              {product.tax_class && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">+ tax</span>
              )}
            </div>

            {/* Variant selector (attributes) */}
            {product.variants && product.variants.length > 0 && (
              <div className="mb-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Variants</div>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v, idx) => (
                    <button
                      key={v.sku || `variant-${idx}`}
                      onClick={() => setSelectedVariantIndex(idx)}
                      className={`px-3 py-2 rounded-full text-sm border ${idx === selectedVariantIndex ? 'bg-gradient-to-r from-red-600 to-black text-white' : 'bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'}`}
                      title={Object.entries(v.attributes || {}).map(([k, val]) => `${k}: ${val}`).join(', ')}
                    >
                      {v.attributes?.size || v.attributes?.color || v.sku || `Variant ${idx + 1}`}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Stock: {variant?.inventory_qty ?? 0} • Policy: {variant?.inventory_policy}</div>
              </div>
            )}

            {/* Quantity & Actions */}
            <div className="space-y-4 mb-6">
              {/* Quantity Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium dark:text-gray-300 mr-2">Quantity:</span>
                <button 
                  className="px-3 py-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600" 
                  onClick={() => setQty(Math.max(1, qty - 1))}
                >
                  -
                </button>
                <input 
                  className="w-16 text-center border dark:border-gray-600 rounded py-2 dark:bg-gray-700 dark:text-gray-300" 
                  type="number" 
                  value={qty} 
                  onChange={(e)=>setQty(Math.max(1, parseInt(e.target.value)||1))} 
                />
                <button 
                  className="px-3 py-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600" 
                  onClick={() => setQty(qty + 1)}
                >
                  +
                </button>
              </div>

              {/* Stock Status */}
              {variant && (
                <div className="mb-4 space-y-2">
                  {variant.inventory_qty > 0 ? (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ In Stock ({variant.inventory_qty} units available)
                    </p>
                  ) : (
                    <p className="text-sm text-red-600 dark:text-red-400 font-bold">
                      ✗ Out of Stock
                    </p>
                  )}
                  
                  {qtyInCart > 0 && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded">
                      📦 {qtyInCart} {qtyInCart === 1 ? 'unit' : 'units'} already in your cart
                    </p>
                  )}
                  
                  {variant.inventory_qty > 0 && variant.inventory_qty <= 5 && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      ⚠ Only {variant.inventory_qty} left in stock - order soon!
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons - Mobile Optimized */}
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                {loading ? (
                  // Loading state - show placeholder
                  <Button 
                    className="w-full py-3 text-base font-semibold"
                    disabled
                  >
                    Loading...
                  </Button>
                ) : variant && variant.inventory_qty === 0 ? (
                  // Out of Stock - Show Notify Me button
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 py-3 text-base font-semibold disabled:opacity-50"
                    onClick={handleRequestRestock}
                    disabled={requestingRestock || hasRestockRequest}
                  >
                    {requestingRestock ? 'Submitting...' : hasRestockRequest ? '✓ You\'ll Be Notified' : '🔔 Notify Me When Available'}
                  </Button>
                ) : (
                  // In Stock - Show normal buttons
                  <>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAddToCart}
                      disabled={cartLoading || !variant}
                    >
                      {cartLoading ? 'Adding...' : 'Add to Cart'}
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 py-3 text-base font-semibold border-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleBuyNow}
                      disabled={cartLoading || !variant}
                    >
                      Buy Now
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-gray-100">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose dark:prose-invert" dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description_html || '<p>No description.</p>') }} />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div className="mt-12">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-2xl dark:text-gray-100">Customer Reviews</CardTitle>
              {product.review_count > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`w-5 h-5 ${
                          star <= (product.rating || 0) 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-semibold dark:text-gray-200">{product.rating?.toFixed(1)}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">based on {product.review_count} reviews</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {reviewsLoading ? (
                <p className="text-center py-8 text-gray-500 dark:text-gray-400">Loading reviews...</p>
              ) : reviews.length === 0 ? (
                <p className="text-center py-8 text-gray-500 dark:text-gray-400">No reviews yet. Be the first to review this product!</p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold dark:text-gray-100">{review.user_name}</span>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= review.rating 
                                      ? 'fill-yellow-400 text-yellow-400' 
                                      : 'text-gray-300 dark:text-gray-600'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(review.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-2">{review.comment}</p>
                      
                      {/* Review Images */}
                      {review.images && review.images.length > 0 && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {review.images.map((imageUrl, idx) => {
                            // Handle relative paths from uploads
                            const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                            const fullImageUrl = imageUrl.startsWith('http') 
                              ? imageUrl 
                              : `${API_BASE}${imageUrl}`;
                            
                            return (
                              <img
                                key={fullImageUrl}
                                src={fullImageUrl}
                                alt={`Review image ${idx + 1}`}
                                className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600 cursor-pointer hover:border-red-400 transition-colors"
                                onClick={() => window.open(fullImageUrl, '_blank')}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
      <Footer />
      
      {/* Stock Insufficient Modal - Center of Screen */}
      {showStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <svg 
                  className="w-8 h-8 text-orange-600 dark:text-orange-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <h3 className="text-xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">
              Insufficient Stock
            </h3>
            
            {/* Message */}
            <div className="text-center text-gray-600 dark:text-gray-300 mb-6 space-y-3">
              <p className="text-lg">
                You requested <span className="font-bold text-red-600 dark:text-red-400">{stockModalData.requested} units</span>
              </p>
              
              {stockModalData.inCart > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    📦 Already in cart: <span className="font-bold">{stockModalData.inCart} {stockModalData.inCart === 1 ? 'unit' : 'units'}</span>
                  </p>
                </div>
              )}
              
              <p className="text-lg">
                Total stock available: <span className="font-bold text-gray-700 dark:text-gray-200">{stockModalData.totalStock} {stockModalData.totalStock === 1 ? 'unit' : 'units'}</span>
              </p>
              
              {stockModalData.available > 0 ? (
                <>
                  <p className="text-lg">
                    Can add: <span className="font-bold text-green-600 dark:text-green-400">{stockModalData.available} more {stockModalData.available === 1 ? 'unit' : 'units'}</span>
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded">
                    We've adjusted the quantity to the maximum you can add.
                  </p>
                </>
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mt-3 p-3 bg-red-50 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-700">
                  ⚠️ Cannot add more items. You already have the maximum available quantity in your cart.
                </p>
              )}
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowStockModal(false)}
                className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {stockModalData.available > 0 ? 'Cancel' : 'Close'}
              </Button>
              {stockModalData.available > 0 && (
                <Button
                  onClick={handleStockModalConfirm}
                  className="flex-1 bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
                >
                  Add {stockModalData.available} to Cart
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductPage;
